import React, { useEffect, useState } from 'react'
import { Calendar, CircleCheck, Clock, Download, EllipsisVertical, Logs, MapPin, Rocket, Users, Users2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { GetEventDetailApi } from '../redux/actions/event-management/EventDetailAction';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import { EditEventAPI } from '../redux/actions/event-management/EditEventAction';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { editEventStateReset } from '../redux/slices/event-management/EditEventSlice';
import { image_url } from '../services/endpoints';
import { GetSportsListApi } from '../redux/actions/event-management/GetSportsListAction';
import { formatDate } from '../helpers/DateFormatter';

const EventDetail = () => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: routeEventId } = useParams();

    const { response, error, loading } = useSelector((state) => state.getEventDetail);
    const { response: updateEventResponse, error: updateEventError, loading: updateEventLoading } = useSelector((state) => state.editEvent);
    const { response: sportsListResponse, error: sportsListError } = useSelector((state) => state.getSportsList)

    const [fetchDetails, setFetchDetails] = useState(false);

    const eventId = location?.state?.eventId || routeEventId;

    const [validationErrors, setValidationErrors] = useState('');

    const [eventType, setEventType] = useState('');

    const [sport, setSport] = useState('');

    const [title, setTitle] = useState('');

    const [description, setDescription] = useState('');

    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');

    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');

    const [locationField, setLocationField] = useState('');

    const [deadline, setDeadline] = useState('');
    const [participants, setParticipants] = useState('');

    const [entry, setEntry] = useState('');

    const [rules, setRules] = useState('');

    const [rewards, setRewards] = useState('');

    const [email, setEmail] = useState('');

    const [phone, setPhone] = useState('');

    const [isPlatformChecked, setIsPlatformChecked] = useState(false);

    const [isFeaturedChecked, setIsFeaturedChecked] = useState(false);

    const [isRegisteredChecked, setIsRegisteredChecked] = useState(false);

    const [isEventChecked, setIsEventChecked] = useState(false);

    const [images, setImages] = useState([]);

    const registeredParticipants = response?.data?.registred_participants || [];

    const getImageUrl = (path, fallback = '/assets/images/dummy-user.png') => {
        if (!path) return fallback;
        if (/^https?:\/\//i.test(path)) return path;
        return `${image_url}${path}`;
    };

    const escapeCsvValue = (value) => {
        const stringValue = value === undefined || value === null ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const handleExportParticipants = () => {
        if (registeredParticipants.length === 0) {
            showErrorToast('No participants available to export');
            return;
        }

        const headers = ['Name', 'Email', 'Status', 'Joined At'];
        const rows = registeredParticipants.map((participant) => [
            participant?.username || 'N/A',
            participant?.email || 'N/A',
            participant?.status || 'N/A',
            participant?.joinedAt ? formatDate(participant.joinedAt) : 'N/A',
        ]);
        const csv = [headers, ...rows]
            .map((row) => row.map(escapeCsvValue).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${response?.data?.title || 'participants'}-participants.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getNextDate = (dateValue) => {
        if (!dateValue) return undefined;
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return undefined;
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    };

    const handlePlatformToggle = () => {
        setIsPlatformChecked(prev => !prev);
    };


    const handleFeaturedToggle = () => {
        setIsFeaturedChecked(prev => !prev);
    };

    const handleRegisteredToggle = () => {
        setIsRegisteredChecked(prev => !prev)
    }

    const handleEventToggle = () => {
        setIsEventChecked(prev => !prev)
    }

    const updateImages = (files) => {
        const newPreviews = files.map(file => ({
            file,
            url: URL.createObjectURL(file),
            isFromServer: false,
        }));

        setImages(newPreviews); // Replace old image with the new one
    };

    const validateForm = () => {
        const newErrors = {};

        if (!eventType) {
            newErrors.eventType = 'Event type is required';
        }

        if (!title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!sport) {
            newErrors.sport = 'Sport is required';
        }

        if (!locationField.trim()) {
            newErrors.locationField = 'Location is required';
        }

        if (eventType === 'challenge' && !endDate) {
            newErrors.endDate = 'End date is required for challenge';
        }

        if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
            newErrors.endDate = 'End date must be greater than start date';
        }

        setValidationErrors(newErrors);

        return Object.keys(newErrors).length === 0; // returns true if no errors
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0]; // only one file

        if (!file) return;

        if (!allowedTypes.includes(file.type)) {
            showErrorToast(`Invalid file type: ${file.name}. Only images are allowed.`);
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            showErrorToast(`File ${file.name} exceeds 2MB size limit.`);
            return;
        }

        updateImages([file]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0]; // only one file

        if (!file) return;

        if (!allowedTypes.includes(file.type)) {
            showErrorToast(`Invalid file type: ${file.name}. Only images are allowed.`);
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            showErrorToast(`File ${file.name} exceeds 2MB size limit.`);
            return;
        }

        updateImages([file]);
    };

    const handleRemoveImage = (indexToRemove) => {
        setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleUpdateEvent = async (id, eventStatusOverride) => {

        console.log("reached")
        if (!validateForm()) return;
        console.log("reached one")
        const formData = new FormData();

        if (images[0]?.file) {
            formData.append('cover_image', images[0].file);
        } else if (images[0]?.isFromServer) {
            formData.append('cover_image', images[0].url);
        }

        // // Append each image file
        // images.forEach((imgObj, index) => {
        //     console.log(imgObj, "imgobj")
        //     formData.append('cover_image', imgObj.file); // Your backend expects req.files
        // });
        formData.append('eventId', id);
        formData.append('event_type', eventType);
        formData.append('sports_category', sport);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('start_date', startDate);
        formData.append('start_time', startTime);
        formData.append('end_date', endDate);
        formData.append('end_time', endTime);
        formData.append('location', locationField);
        formData.append('max_participants', participants);
        formData.append('registration_deadline', deadline);
        formData.append('entry_requirement', entry);
        formData.append('prize', rewards);
        formData.append('contact_email', email);
        formData.append('contact_number', phone);
        formData.append('platform_event', isPlatformChecked);
        formData.append('featured_event', isFeaturedChecked);
        formData.append('allow_registration', isRegisteredChecked);
        formData.append('public_event', isEventChecked);
        formData.append('event_status', eventStatusOverride || response?.data?.event_status || 'published');

        try {
            await dispatch(EditEventAPI(formData))
        } catch (error) {
            showErrorToast('error in creating event')
        }
    }

    useEffect(() => {
        dispatch(GetSportsListApi());
    }, [dispatch])

    useEffect(() => {
        if (eventId) {
            dispatch(GetEventDetailApi(eventId))
        }
    }, [dispatch, eventId, fetchDetails])

    useEffect(() => {
        if (response?.data) {
            setEventType(response.data.event_type);

            setTitle(response?.data?.title);
            setDescription(response?.data?.description);

            const startDateRaw = response?.data?.start_date;
            const endDateRaw = response?.data?.end_date;
            const deadlineRaw = response?.data?.registration_deadline

            const isoStartDate = startDateRaw && !isNaN(new Date(startDateRaw))
                ? new Date(startDateRaw).toISOString().split('T')[0]
                : ''; // fallback value if invalid

            const isoEndDate = endDateRaw && !isNaN(new Date(endDateRaw))
                ? new Date(endDateRaw).toISOString().split('T')[0]
                : ''; // fallback value if invalid

            const isoDeadline = deadlineRaw && !isNaN(new Date(deadlineRaw))
                ? new Date(deadlineRaw).toISOString().split('T')[0]
                : ''; // fallback value if invalid

            // const formattedStartDate = startDateRaw && !isNaN(new Date(startDateRaw))
            //     ? new Date(startDateRaw).toLocaleDateString('en-US', {
            //         year: 'numeric',
            //         month: 'long',
            //         day: 'numeric',
            //     })
            //     : '';

            // const formattedEndDate = endDateRaw && !isNaN(new Date(endDateRaw))
            //     ? new Date(endDateRaw).toLocaleDateString('en-US', {
            //         year: 'numeric',
            //         month: 'long',
            //         day: 'numeric',
            //     })
            //     : '';

            const formattedStartTime = response?.data?.start_time?.slice(0, 5) || '';
            const formattedEndTime = response?.data?.end_time?.slice(0, 5) || '';

            setStartDate(isoStartDate);
            setStartTime(formattedStartTime);
            setEndDate(isoEndDate);
            setEndTime(formattedEndTime);
            setLocationField(response?.data?.location_name);
            setDeadline(isoDeadline);
            setParticipants(response?.data?.max_participants);
            setEntry(response?.data?.entry_requirement);
            setRewards(response?.data?.prize);
            setEmail(response?.data?.contact_email);
            setPhone(response?.data?.contact_number);
            setIsPlatformChecked(response?.data?.platform_event);
            setIsFeaturedChecked(response?.data?.featured_event);
            setIsRegisteredChecked(response?.data?.allow_registration);
            setIsEventChecked(response?.data?.public_event);

            setImages([
                {
                    file: null, // existing image from server
                    url: `${image_url}${response.data.cover_image}`, // this is the existing image URL
                    isFromServer: true, // track if it's from server
                }
            ]);

            const matchedSport = sportsListResponse?.data?.find(
                (s) => s.name === response.data.sports_category
            );

            if (matchedSport) {
                setSport(matchedSport.id);
            }
        }
    }, [response, sportsListResponse]);

    useEffect(() => {
        if (updateEventResponse?.status === true) {
            showSuccessToast(updateEventResponse?.message);
            dispatch(editEventStateReset());
            setFetchDetails(prev => !prev)
        }
    }, [updateEventResponse, dispatch])

    useEffect(() => {
        if (updateEventError) {
            showErrorToast(updateEventError?.data);
            dispatch(editEventStateReset());
        }
    }, [updateEventError, dispatch])

    return (
        <>
            {loading
                ? (
                    <div style={{ height: '70vh' }} className='d-flex align-items-center justify-content-center'>
                        <PulseLoader size={25} color="#0f0a30" />
                    </div>
                ) : error ? (
                    <div style={{ height: '70vh' }} className='d-flex align-items-center justify-content-center'>
                        <p className='text-danger'>Something went wrong</p>
                    </div>
                ) : <>
                    <div className="page_top_section">
                        <div className="page-header">
                            <div className="page-header-left d-flex align-items-center">
                                <div className="page-header-title">
                                    <nav class="breadcrumb_nav mb-3">
                                        <ul class="breadcrumb mb-0">
                                            <li class="breadcrumb-item"><Link to='/admin-dashboard/event-and-challenge-management'>Event & Challenge Management</Link></li>
                                            <li class="breadcrumb-item text-capitalize">Edit Event</li>
                                        </ul>
                                    </nav>
                                    <h1 className="fs-3 text-black fw-semibold  mb-0 text-capitalize">Edit Event</h1>
                                    <p className="text-clr mb-0">Update event details and settings.</p>
                                </div>
                            </div>
                            <div className="page-header-right ms-auto">
                                <div className="page-header-right-items ">
                                    <div className="d-flex rtgl-d-none">
                                        <button className="page-header-right-close-toggle border-0 bg-transparent" type="button" onClick={() => navigate('/admin-dashboard/event-and-challenge-management')}>
                                            <svg stroke="currentColor" fill="none" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="me-2" height={16} width={16} xmlns="http://www.w3.org/2000/svg">
                                                <line x1={19} y1={12} x2={5} y2={12} />
                                                <polyline points="12 19 5 12 12 5" />
                                            </svg><span>Back</span></button>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 page-header-right-items-wrapper">
                                        <div className="side-content-blk">
                                            <button className="outline-btn" type="button" onClick={() => navigate('/admin-dashboard/event-and-challenge-management')}>
                                                <svg stroke="currentColor" fill="none" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height={16} width={16} xmlns="http://www.w3.org/2000/svg">
                                                    <line x1={19} y1={12} x2={5} y2={12} />
                                                    <polyline points="12 19 5 12 12 5" />
                                                </svg>
                                                <span className="btntext">Back to Events</span>
                                            </button>
                                            {/* <button className="outline-btn">
                                                <Trash2Icon />
                                                <span className="btntext">Delete Event</span>
                                            </button> */}
                                            {/* <button className="primary-btn">
                                                <span className="btntext">Save Changes</span>
                                            </button> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="rtgl-d-none d-flex align-items-center">
                                <button className="page-header-right-open-toggle border-0 bg-transparent" type="button">
                                    <Logs />
                                </button>
                            </div>

                        </div>
                    </div>

                    <section className="main-section common_section spacer">
                        <div className="container">
                            <div className="row gy-4">
                                <div className="col-12">
                                    <div className="row gx-4 gy-4">
                                        <div className="col-sm-12 col-lg-8 col-xl-8">
                                            <div className='outline_blk'>
                                                <div className='hstack justify-content-between gap-3 flex-wrap mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-4'>Event Status</h2>
                                                </div>
                                                <div className='row g-3'>
                                                    <div className='col-sm-4'>
                                                        <div className='event_status_info'>
                                                            <h3><CircleCheck size={20} />{response?.data?.event_status ?
                                                                response.data.event_status.charAt(0).toUpperCase() + response.data.event_status.slice(1) : ''}</h3>
                                                            <p>Event is live and accepting registrations</p>
                                                        </div>
                                                    </div>
                                                    <div className='col-sm-4'>
                                                        <div className='event_status_info'>
                                                            <h3><Users size={20} /> {response?.data?.registred_participants_count || 0} Registered</h3>
                                                            <p>Out of {response?.data?.max_participants || 0} maximum participants</p>
                                                        </div>
                                                    </div>
                                                    <div className='col-sm-4'>
                                                        <div className='event_status_info'>
                                                            <h3><Clock size={20} />{response?.data?.days_left || 0} Days Left</h3>
                                                            <p>Until registration deadline</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-4'>Basic Information</h2>
                                                </div>
                                                <div className='row g-3'>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Event Type *</label>
                                                            <div className="input-field">
                                                                <select className="form-select" value={eventType} onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    setEventType(value);
                                                                    if (validationErrors.eventType) {
                                                                        setValidationErrors(prev => ({ ...prev, eventType: false }));
                                                                    }
                                                                }}>
                                                                    <option value="">Select type</option>
                                                                    <option value="event">Event</option>
                                                                    <option value="challenge">Challenge</option>
                                                                </select>
                                                            </div>
                                                            {validationErrors.eventType &&
                                                                (
                                                                    <span className={`error ${validationErrors.eventType ? '' : 'd-none'}`}>{validationErrors.eventType}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Sport Category *</label>
                                                            <div className="input-field">
                                                                <select className="form-select" value={sport} onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    setSport(value);
                                                                    if (validationErrors.sport) {
                                                                        setValidationErrors(prev => ({ ...prev, sport: false }));
                                                                    }
                                                                }}>
                                                                    <option value="">All Sport</option>
                                                                    {sportsListResponse && sportsListResponse?.data?.length > 0 ? (
                                                                        sportsListResponse?.data?.map((sport, index) => (
                                                                            <option key={index} value={sport.id}>
                                                                                {sport.name}
                                                                            </option>
                                                                        ))
                                                                    ) : sportsListError ? (
                                                                        <option disabled>No sports available</option>
                                                                    ) : <option disabled>No sports available</option>}
                                                                </select>
                                                            </div>
                                                            {validationErrors.sport &&
                                                                (
                                                                    <span className={`error ${validationErrors.sport ? '' : 'd-none'}`}>{validationErrors.sport}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Event Title *</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="text" value={title} onKeyDown={(e) => {
                                                                    // Block spacebar if the field is empty (no characters yet)
                                                                    if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                        e.preventDefault(); // 🔥 prevents the space from being typed
                                                                    }
                                                                }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setTitle(value);
                                                                        if (validationErrors.title) {
                                                                            setValidationErrors(prev => ({ ...prev, title: false }));
                                                                        }
                                                                    }} />
                                                            </div>
                                                            {validationErrors.title &&
                                                                (
                                                                    <span className={`error ${validationErrors.title ? '' : 'd-none'}`}>{validationErrors.title}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Description *</label>
                                                            <div className="input-field">
                                                                <textarea className="form-control" placeholder="Provide detailed description of the event" value={description} onKeyDown={(e) => {
                                                                    // Block spacebar if the field is empty (no characters yet)
                                                                    if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                        e.preventDefault(); // 🔥 prevents the space from being typed
                                                                    }
                                                                }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setDescription(value);
                                                                        if (validationErrors.description) {
                                                                            setValidationErrors(prev => ({ ...prev, description: false }));
                                                                        }
                                                                    }}>Join us for an exciting summer football championship featuring local teams from across the city. This tournament will showcase the best talent in amateur football with exciting matches and great prizes.</textarea>
                                                            </div>
                                                            {validationErrors.description &&
                                                                (
                                                                    <span className={`error ${validationErrors.description ? '' : 'd-none'}`}>{validationErrors.description}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Event Cover Image</label>
                                                            <div className="input-field" onDrop={handleDrop}
                                                                onDragOver={handleDragOver}>
                                                                <input className="form-control d-none" type="file" id="browseFile" onChange={handleFileChange} accept="image/jpeg, image/png, image/jpg, image/webp, image/svg+xml" />
                                                                <label htmlFor='browseFile' className='browse_file_label'>
                                                                    <div className='file_preview position-relative'>
                                                                        {images.map((img, index) => (
                                                                            <>
                                                                                <img
                                                                                    src={img.url}
                                                                                    alt={`preview-${index}`}
                                                                                    className='file_preview_img'
                                                                                />
                                                                                <button
                                                                                    type='button'
                                                                                    onClick={() => handleRemoveImage(index)}
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        top: '-6px',
                                                                                        right: '-6px',
                                                                                        backgroundColor: '#fff',
                                                                                        border: '1px solid #ccc',
                                                                                        borderRadius: '50%',
                                                                                        width: '20px',
                                                                                        height: '20px',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        fontSize: '12px',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                >
                                                                                    &times;
                                                                                </button>
                                                                            </>
                                                                        ))}
                                                                    </div>
                                                                    <p>Drop your image here, or browse</p>
                                                                    <span>Supports: JPG, PNG, GIF up to 10MB</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-4'>Event Details</h2>
                                                </div>
                                                <div className='row g-3'>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Start Date *</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="date" value={startDate}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setStartDate(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                        <div className="form-group mt-3">
                                                            <label className="fs-12 fw-semibold mb-1">End Date</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="date" value={endDate}
                                                                    min={getNextDate(startDate)}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setEndDate(value);
                                                                        if (validationErrors.endDate) {
                                                                            setValidationErrors(prev => ({ ...prev, endDate: false }));
                                                                        }
                                                                    }} />
                                                            </div>
                                                            {validationErrors.endDate &&
                                                                (
                                                                    <span className={`error ${validationErrors.endDate ? '' : 'd-none'}`}>{validationErrors.endDate}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Start Time *</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="time" value={startTime}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setStartTime(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                        <div className="form-group mt-3">
                                                            <label className="fs-12 fw-semibold mb-1">End Time</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="time" value={endTime}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setEndTime(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Location *</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter venue or location" type="text" value={locationField}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setLocationField(value);
                                                                        if (validationErrors.locationField) {
                                                                            setValidationErrors(prev => ({ ...prev, locationField: false }));
                                                                        }
                                                                    }} />
                                                            </div>
                                                            {validationErrors.locationField &&
                                                                (
                                                                    <span className={`error ${validationErrors.locationField ? '' : 'd-none'}`}>{validationErrors.locationField}</span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Maximum Participants</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter max participants" type="number" value={participants}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setParticipants(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Registration Deadline</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Enter event title" type="date" value={deadline}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setDeadline(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-4'>Additional Settings</h2>
                                                </div>
                                                <div className='row g-3'>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Entry Requirements</label>
                                                            <div className="input-field">
                                                                <textarea className="form-control" placeholder="Any specific requirements for participants"
                                                                    value={entry}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setEntry(value);
                                                                    }}></textarea>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Rules & Guidelines</label>
                                                            <div className="input-field">
                                                                <textarea className="form-control" placeholder="Event rules and guidelines" value={rules}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setRules(value);
                                                                    }}></textarea>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-12'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Prizes & Rewards</label>
                                                            <div className="input-field">
                                                                <textarea className="form-control" placeholder="Describe prizes and rewards (if any)" value={rewards}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setRewards(value);
                                                                    }}></textarea>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Contact Email</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Contact email for inquiries" type="text" value={email} onKeyDown={(e) => {
                                                                    // Block spacebar if the field is empty (no characters yet)
                                                                    if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                        e.preventDefault(); // 🔥 prevents the space from being typed
                                                                    }
                                                                }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setEmail(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className="form-group">
                                                            <label className="fs-12 fw-semibold mb-1">Contact Phone</label>
                                                            <div className="input-field">
                                                                <input className="form-control" placeholder="Contact phone number" type="text" value={phone}
                                                                    onKeyDown={(e) => {
                                                                        // Block spacebar if the field is empty (no characters yet)
                                                                        if (e.key === ' ' && e.currentTarget.value.trim() === '') {
                                                                            e.preventDefault(); // 🔥 prevents the space from being typed
                                                                        }
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setPhone(value);
                                                                    }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='hstack justify-content-between gap-3 flex-wrap mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-4'>Registered Participants</h2>
                                                    <button className="outline-btn" type="button" onClick={handleExportParticipants}>
                                                        <Download />
                                                        <span className="btntext">Export List</span>
                                                    </button>
                                                </div>
                                                <div className='registered_participant_blk'>
                                                    {registeredParticipants.length > 0 ? registeredParticipants.map((p, index) => {
                                                        return (
                                                            <div className='registered_participant_item hstack justify-content-between gap-3 flex-wrap inner_outline_blk' key={p?.userId || index}>
                                                                <div className='registered_user_blk'>
                                                                    <div className='registered_user_img'>
                                                                        <img src={getImageUrl(p?.profilePic)} alt={p?.username || 'Participant'} />
                                                                    </div>
                                                                    <div className='registered_user_info'>
                                                                        <h3>{p?.username || 'N/A'}</h3>
                                                                        <p>{p?.email || 'N/A'}</p>
                                                                    </div>
                                                                </div>
                                                                <div className='registered_blk_action'>
                                                                    <span className='common_badges text-capitalize'>{p?.status || 'joined'}</span>
                                                                    <button className='option_btn border-0 bg-white'><EllipsisVertical /></button>
                                                                </div>
                                                            </div>
                                                        )
                                                    }) :
                                                        (
                                                            <h2 className='text-center'>No data available</h2>
                                                        )
                                                    }


                                                </div>
                                                {registeredParticipants.length > 0 &&
                                                    <div className='hstack justify-content-center gap-3 mt-3'>
                                                        <button type="button" className='fs-14 fw-medium text-clr text-decoration-underline border-0 bg-transparent'>Showing all {registeredParticipants.length} participants</button>
                                                    </div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-lg-4 col-xl-4">
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-5'>Event Settings</h2>
                                                </div>
                                                <div className='event_setting_toggle'>
                                                    <div className='event_setting_item hstack justify-content-between align-items-center gap-3'>
                                                        <div className='event_setting_info'>
                                                            <h3>Platform Event</h3>
                                                            <p>Mark as official platform event</p>
                                                        </div>
                                                        <div className='toggle-box'>
                                                            <input class="d-none" type="checkbox" id="checkbox1" checked={isPlatformChecked}
                                                                onChange={handlePlatformToggle} />
                                                            <label htmlFor="checkbox1"></label>
                                                        </div>
                                                    </div>
                                                    <div className='event_setting_item hstack justify-content-between align-items-center gap-3'>
                                                        <div className='event_setting_info'>
                                                            <h3>Featured Event</h3>
                                                            <p>Show in featured section</p>
                                                        </div>
                                                        <div className='toggle-box'>
                                                            <input class="d-none" type="checkbox" id="checkbox2" checked={isFeaturedChecked}
                                                                onChange={handleFeaturedToggle} />
                                                            <label htmlFor="checkbox2"></label>
                                                        </div>
                                                    </div>
                                                    <div className='event_setting_item hstack justify-content-between align-items-center gap-3'>
                                                        <div className='event_setting_info'>
                                                            <h3>Allow Registration</h3>
                                                            <p>Users can register for event</p>
                                                        </div>
                                                        <div className='toggle-box'>
                                                            <input class="d-none" type="checkbox" id="checkbox3" checked={isRegisteredChecked}
                                                                onChange={handleRegisteredToggle} />
                                                            <label htmlFor="checkbox3"></label>
                                                        </div>
                                                    </div>
                                                    <div className='event_setting_item hstack justify-content-between align-items-center gap-3'>
                                                        <div className='event_setting_info'>
                                                            <h3>Public Event</h3>
                                                            <p>Visible to all users</p>
                                                        </div>
                                                        <div className='toggle-box'>
                                                            <input class="d-none" type="checkbox" id="checkbox4" checked={isEventChecked}
                                                                onChange={handleEventToggle} />
                                                            <label htmlFor="checkbox4"></label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 class="mb-0 fw-semibold fs-5">Event Analytics</h2>
                                                </div>
                                                <div class="info-listing">
                                                    <h3>No data available</h3>
                                                </div>
                                                {/* <div class="info-listing">
                                                    <div className='info-listing_item hstack justify-content-between gap-3'>
                                                        <span class="info-listing-title">Total Views</span>
                                                        <span class="info-listing-value">1247</span>
                                                    </div>
                                                    <div className='info-listing_item hstack justify-content-between gap-3'>
                                                        <span class="info-listing-title">Registrations</span>
                                                        <span class="info-listing-value">15</span>
                                                    </div>
                                                    <div className='info-listing_item hstack justify-content-between gap-3'>
                                                        <span class="info-listing-title">Conversion Rate</span>
                                                        <span class="info-listing-value">14.8%</span>
                                                    </div>
                                                    <div className='info-listing_item hstack justify-content-between gap-3'>
                                                        <span class="info-listing-title">Shares</span>
                                                        <span class="info-listing-value">18</span>
                                                    </div>
                                                </div>
                                                <div className='hstack justify-content-center gap-3 mt-3'>
                                                    <a href='javascript:void(0)' className='fs-14 fw-medium text-clr text-decoration-underline'>View detailed analytics</a>
                                                </div> */}
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-5'>Preview</h2>
                                                </div>
                                                <div className='inner_outline_blk'>
                                                    <div className='event_preview_cover'>
                                                        {images.map((img, index) => (
                                                            <img src={img.url} key={index} className='file_preview_img' alt="Event preview" />
                                                        ))}
                                                        {/* <span>Event Cover Preview</span> */}
                                                    </div>
                                                    <h3>Event Title</h3>
                                                    <p className='ev_pre_info'><Calendar />{(startDate || startTime) ? `${startDate} ${startTime}` : 'Date & Time'}</p>
                                                    <p className='ev_pre_info'><MapPin /> {locationField ? locationField : 'Location'}</p>
                                                    <p className='ev_pre_info'><Users2 />  {participants ? participants : 'Participants'}</p>
                                                    <p className='text-truncate fs-14 mt-2 mb-0'>{description ? description : 'Event description will appear here...'}</p>
                                                    <div className='mt-3'>
                                                        <button className="primary-btn w-100 disabled" disabled>
                                                            <span className="btntext" style={{ cursor: 'not-allowed' }}>Register Now</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='outline_blk'>
                                                <div className='mb-3'>
                                                    <h2 className='mb-0 fw-semibold fs-5'>Actions</h2>
                                                </div>
                                                <div className='vstack gap-3'>
                                                    <button className="primary-btn w-100" onClick={() => handleUpdateEvent(response?.data?._id)}>
                                                        <Rocket />
                                                        <span className="btntext">{updateEventLoading ? <PulseLoader color="#ffffff" /> : 'Update Event'}</span>
                                                    </button>
                                                    {response?.data?.event_status === 'save_as_draft' && (
                                                        <button className="outline-btn w-100" onClick={() => handleUpdateEvent(response?.data?._id, 'published')}>
                                                            <Rocket />
                                                            <span className="btntext">{updateEventLoading ? <PulseLoader color="#000000" /> : 'Publish Event'}</span>
                                                        </button>
                                                    )}
                                                    {/* <button className="outline-btn w-100">
                                                        <Eye />
                                                        <span className="btntext">Preview Event</span>
                                                    </button> */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </section>
                </>
            }
        </>
    )
}

export default EventDetail
